/**
 * Hook pour synchroniser l'onglet sidebar actif avec l'URL du navigateur.
 *
 * Fix backlog 1.2 (19-05) : permet le deeplinking et l'usage des boutons
 * back/forward du navigateur sans refactor en `react-router-dom`. Couplé à
 * l'état `activeSidebarTab` côté `DashboardPage`.
 *
 * Mapping URL ↔ tab id (cf. SIDEBAR_ITEMS dans `utils/Constants.tsx`) :
 *   /                → null (dashboard)
 *   /positioning     → positionnement
 *   /watch           → veille
 *   /sources         → sources
 *   /channels        → canaux
 *   /automation      → automatisations
 *   /newsletter      → newsletter
 *   /account         → compte
 *   /settings        → settings
 *   /spaces          → espaces
 *
 * Comportement :
 *   - Au mount, lit l'URL et appelle setActiveTab(tabId) si correspondance.
 *   - À chaque changement de `activeTab`, fait un history.replaceState (pas
 *     pushState : on évite que chaque switch onglet pollue l'historique).
 *   - On exclut les routes spéciales (oauth callback, verify-email, etc.) qui
 *     ont leur propre logique de redirection dans App.tsx.
 */

import { useEffect, useRef } from 'react';

// Mapping bidirectionnel URL ↔ tab id
const URL_TO_TAB: Record<string, string> = {
  '/positioning': 'positionnement',
  '/watch': 'veille',
  '/sources': 'sources',
  '/channels': 'canaux',
  '/automation': 'automatisations',
  '/newsletter': 'newsletter',
  '/analytics': 'analytics',
  '/engagement': 'engagement',
  '/account': 'compte',
  '/settings': 'settings',
  '/spaces': 'espaces',
};

const TAB_TO_URL: Record<string, string> = Object.fromEntries(
  Object.entries(URL_TO_TAB).map(([url, tab]) => [tab, url])
);

// V4-E2E-2026-06-11-02 — paths applicatifs valides pour un user authentifié.
// Source de vérité unique (keys de URL_TO_TAB) + le dashboard racine. Utilisé par
// App.tsx pour décider d'afficher une 404 sur une route inconnue.
export const KNOWN_APP_PATHS: string[] = [
  '/',
  '/login',
  '/dashboard',
  '/history',
  ...Object.keys(URL_TO_TAB),
];

export function isKnownAppPath(pathname: string): boolean {
  // '/' est un match exact (sinon "/" préfixerait toutes les routes). Les autres
  // acceptent un éventuel sous-chemin (ex: /positioning/xxx).
  return KNOWN_APP_PATHS.some(p =>
    p === '/' ? pathname === '/' : pathname === p || pathname.startsWith(p + '/'),
  );
}

// Routes spéciales gérées ailleurs (App.tsx) — on ne touche pas à l'URL pour
// ces chemins, même si l'utilisateur reste sur la page après authent.
const RESERVED_PATHS = [
  '/oauth/callback',
  '/socials/callback',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
  '/login',
];

function isReservedPath(pathname: string): boolean {
  return RESERVED_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'));
}

export function useUrlSyncedTab(
  activeTab: string | null,
  setActiveTab: (tab: string | null) => void,
): void {
  // Ref pour ignorer la 1ère écriture (le mount d'`activeTab=null` ne doit
  // pas overwrite l'URL si l'utilisateur arrive sur /positioning par exemple).
  const isInitialMount = useRef(true);

  // 1. Lecture initiale URL → set activeTab (deeplinking)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname;
    if (isReservedPath(path)) return;
    const tabFromUrl = URL_TO_TAB[path];
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    // Pas de dependency activeTab : ne s'exécute qu'au mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Écriture activeTab → URL (à chaque change)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const currentPath = window.location.pathname;
    if (isReservedPath(currentPath)) return;
    const targetPath = activeTab ? TAB_TO_URL[activeTab] ?? '/' : '/';
    if (currentPath !== targetPath) {
      window.history.replaceState({}, '', targetPath);
    }
  }, [activeTab]);

  // 3. Gestion des back/forward (popstate event)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handlePopState = () => {
      const path = window.location.pathname;
      if (isReservedPath(path)) return;
      const tabFromUrl = URL_TO_TAB[path] ?? null;
      setActiveTab(tabFromUrl);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [setActiveTab]);
}
